var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faTrashCan } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormControlLabel, IconButton, Switch, styled } from '@mui/material';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StatusPermission } from '../../../../enum';
import { useUserPermissionsAPI } from '../../../app/selectors';
import { Autocomplete, ConfirmDialog, FormField, NativeNumberInput, TableCell, TableRow } from '../../../common';
import { TableHierarchyCell } from '../../../common/tableHierarchy';
import { useCurrencySymbol, useFormlessContext } from '../../../hooks';
import { useEffectivePlanPermission, useEffectiveQueueLocations, useWageTypeOptions } from '../../selectors';
import { useRoleAppliesToOptions } from '../selectors';
import { RoleAppliesTo, StaffCalculate } from '../types';
export var StyledAgentCell = styled(TableCell)(function (_a) {
  var theme = _a.theme;
  return {
    backgroundColor: theme.palette.background.default
  };
});
var DeleteIcon = styled(FontAwesomeIcon, {
  shouldForwardProp: function (prop) {
    return prop !== 'disabled';
  }
})(function (_a) {
  var theme = _a.theme,
    disabled = _a.disabled;
  return {
    color: disabled ? theme.palette.text.disabled : theme.palette.text.primary
  };
});
export var RoleRow = function (_a) {
  var _b, _c, _d, _e, _f;
  var formPath = _a.formPath,
    item = _a.item,
    disabled = _a.disabled,
    isAgent = _a.isAgent,
    onDelete = _a.onDelete,
    disableFinancial = _a.disableFinancial,
    disableSupportStaff = _a.disableSupportStaff,
    isReview = _a.isReview,
    currencyCode = _a.currencyCode,
    _g = _a.indented,
    indented = _g === void 0 ? false : _g;
  var t = useTranslation('wizard', {
    keyPrefix: 'supportStaff'
  }).t;
  var _h = (_b = useFormlessContext()) !== null && _b !== void 0 ? _b : {},
    control = _h.control,
    setValue = _h.setValue;
  var locations = useEffectiveQueueLocations();
  var hasLocations = !!locations.length;
  var clearErrors = useFormContext().clearErrors;
  // Watch the sibling array so field values stay reactive after any update
  var siblingsPath = formPath.replace(/\.\d+$/, '');
  var roles = useWatch({
    control: control,
    name: siblingsPath
  });
  var rowIdx = Number(formPath.split('.').pop());
  var roleAppliesToOptions = useRoleAppliesToOptions();
  var wageTypeOptions = useWageTypeOptions();
  var financialPermission = useUserPermissionsAPI().financialPermission;
  var effectiveFinancialPermission = useEffectivePlanPermission(financialPermission);
  var disableEditFinancial = effectiveFinancialPermission !== StatusPermission.Edit;
  var _j = useState(false),
    showAttention = _j[0],
    setShowAttention = _j[1];
  var _k = useCurrencySymbol(currencyCode),
    currencySymbol = _k.currencySymbol,
    prefix = _k.prefix,
    suffix = _k.suffix;
  var handleSwitchChange = function (event) {
    var _a, _b;
    var newValue = event.target.checked ? StaffCalculate.Headcount : StaffCalculate.FTE;
    setValue("".concat(formPath, ".staffCalculate"), newValue, {
      shouldDirty: true
    });
    // Warn the user if an existing ratio value will be affected by the mode change
    setShowAttention(!!((_b = (_a = roles === null || roles === void 0 ? void 0 : roles[rowIdx]) === null || _a === void 0 ? void 0 : _a.ratio) !== null && _b !== void 0 ? _b : item.ratio));
  };
  var handleRoleAppliesToChange = function (value) {
    setValue("".concat(formPath, ".roleAppliesTo"), value, {
      shouldDirty: true
    });
    clearErrors(siblingsPath);
  };
  var handleWageTypeChange = function (value) {
    return setValue("".concat(formPath, ".wageType"), value, {
      shouldDirty: true
    });
  };
  return _jsxs(_Fragment, {
    children: [_jsxs(TableRow, {
      children: [indented ? _jsx(TableHierarchyCell, __assign({
        align: "left",
        nestLevel: 4
      }, {
        children: item.role
      })) : _jsx(TableCell, __assign({
        align: "left",
        greyColor: true,
        sx: {
          fontWeight: 600
        }
      }, {
        children: item.role
      })), !disableSupportStaff && _jsxs(_Fragment, {
        children: [isAgent ? _jsx(StyledAgentCell, {}) : _jsx(TableCell, __assign({
          padding: "none"
        }, {
          children: _jsx(FormField, __assign({
            disableHelperText: true,
            name: "".concat(formPath, ".roleAppliesTo")
          }, {
            children: _jsx(Autocomplete, {
              disabled: disabled || !hasLocations,
              options: roleAppliesToOptions,
              value: (_d = (_c = roles === null || roles === void 0 ? void 0 : roles[rowIdx]) === null || _c === void 0 ? void 0 : _c.roleAppliesTo) !== null && _d !== void 0 ? _d : RoleAppliesTo.Queue,
              onChange: handleRoleAppliesToChange,
              size: "small"
            })
          }))
        })), isAgent ? _jsx(StyledAgentCell, {}) : _jsx(TableCell, __assign({
          padding: "none"
        }, {
          children: _jsx(FormField, __assign({
            disableHelperText: true,
            name: "".concat(formPath, ".ratio")
          }, {
            children: _jsx(NativeNumberInput, {
              disabled: disabled,
              max: 1000,
              decimal: 0
            })
          }))
        })), isAgent ? _jsx(StyledAgentCell, {}) : _jsxs(TableCell, __assign({
          padding: "none",
          sx: {
            minWidth: '10rem'
          }
        }, {
          children: [_jsx(FormControlLabel, {
            sx: {
              marginLeft: 0
            },
            control: _jsx(Switch, {
              disabled: disabled,
              checked: ((_e = roles === null || roles === void 0 ? void 0 : roles[rowIdx]) === null || _e === void 0 ? void 0 : _e.staffCalculate) === StaffCalculate.Headcount,
              onChange: handleSwitchChange
            }),
            label: 'FTE',
            labelPlacement: "start"
          }), "\u00A0 ", t('headcount')]
        }))]
      }), !disableFinancial && _jsxs(_Fragment, {
        children: [_jsx(TableCell, __assign({
          padding: "none",
          sx: {
            minWidth: '9rem'
          }
        }, {
          children: _jsx(FormField, __assign({
            disableHelperText: true,
            name: "".concat(formPath, ".wageType")
          }, {
            children: _jsx(Autocomplete, {
              disabled: disabled || disableEditFinancial,
              options: wageTypeOptions,
              value: (_f = roles === null || roles === void 0 ? void 0 : roles[rowIdx]) === null || _f === void 0 ? void 0 : _f.wageType,
              onChange: handleWageTypeChange,
              size: "small"
            })
          }))
        })), _jsx(TableCell, __assign({
          padding: "none"
        }, {
          children: _jsx(FormField, __assign({
            disableHelperText: true,
            name: "".concat(formPath, ".salary")
          }, {
            children: _jsx(NativeNumberInput, {
              disabled: disabled || disableEditFinancial,
              decimal: 2,
              min: 1,
              max: 10000000,
              placeholder: currencySymbol,
              prefix: prefix,
              suffix: suffix
            })
          }))
        })), _jsx(TableCell, __assign({
          padding: "none",
          sx: {
            maxWidth: '6rem'
          }
        }, {
          children: _jsx(FormField, __assign({
            disableHelperText: true,
            name: "".concat(formPath, ".loadFactor")
          }, {
            children: _jsx(NativeNumberInput, {
              disabled: disabled || disableEditFinancial,
              decimal: 2,
              min: 0,
              max: 100,
              suffix: "%"
            })
          }))
        }))]
      }), !disableSupportStaff && !isReview && _jsx(TableCell, __assign({
        padding: "none",
        align: "center"
      }, {
        children: _jsx(IconButton, __assign({
          disabled: disabled || isAgent,
          size: "small",
          onClick: onDelete
        }, {
          children: _jsx(DeleteIcon, {
            icon: faTrashCan
          })
        }))
      }))]
    }), _jsx(ConfirmDialog, {
      open: showAttention,
      onClose: function () {
        return setShowAttention(false);
      },
      title: t('attentionAlert.title'),
      message: t('attentionAlert.content'),
      cancelButtonText: "close"
    })]
  });
};